import React, { FC, useState } from "react";
import { Loader } from "shared/ui";
import {
  OrderProcessInfoBox,
  OrderStepTrackerResponseItem,
  useStartWorkingOnProcessMutation,
} from "shared/api";
import { useModal } from "shared/hooks";
import { ProducedItemsInOrdersModal } from "./ProducedItemsInOrdersModal";
import { ProdPageHeader } from "./ProdPageHeader";
import { ProdPageContent } from "./ProdPageContent";

interface DynamicProcessStepsForTheOrderProps {
  processStepId?: number;
  orderId?: string;
  orderProcessStepInfoBox?: OrderProcessInfoBox;
  incrementProcessStepIndex: () => void;
  decrementProcessStepIndex: () => void;
  orderStepTrackerResponseItem?: OrderStepTrackerResponseItem;
  isLoadingOrderData: boolean;
  clearSelectedOrder: () => void;
  refetchOrderProcessStepTracker: () => void;
  isLoadingOrderCodes: boolean;
  navigateOrder: (direction: "next" | "previous") => void;
  productCardId: number;
}

const DynamicProcessStepsForTheOrder: FC<
  DynamicProcessStepsForTheOrderProps
> = ({
  processStepId,
  orderId,
  incrementProcessStepIndex,
  decrementProcessStepIndex,
  orderStepTrackerResponseItem,
  isLoadingOrderData,
  clearSelectedOrder,
  refetchOrderProcessStepTracker,
  isLoadingOrderCodes,
  navigateOrder,
  orderProcessStepInfoBox,
  productCardId,
}) => {
  const [locked, setLocked] = useState(true);
  const [startWorkingOnProcess] = useStartWorkingOnProcessMutation();

  const startProcessAndUnlock = async (id: number) => {
    const payload = {
      machine: orderStepTrackerResponseItem?.machine || null,
      start_datetime: orderStepTrackerResponseItem?.start_datetime || null,
      end_datetime: orderStepTrackerResponseItem?.end_datetime || null,
      status: orderStepTrackerResponseItem?.status || null,
      info: orderStepTrackerResponseItem?.info ?? "",
    };

    try {
      await startWorkingOnProcess({ id, data: payload }).unwrap();
      setLocked(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to start process:", error);
    }
  };

  const handleLocked = () => {
    const id = orderStepTrackerResponseItem?.id;
    if (typeof id === "number") {
      startProcessAndUnlock(id);
    } else {
      // eslint-disable-next-line no-console
      console.error("ID is undefined. Cannot start process.");
    }
  };

  const [
    producedItemInOrderModal,
    openProducedItemInOrderModal,
    hideProducedItemInOrderModal,
  ] = useModal();

  const closeProdPageModal = () => {
    hideProducedItemInOrderModal();
    clearSelectedOrder();
  };

  const [showMaintenanceIcon, setShowMaintenanceIcon] = useState(false);

  const handleshowMaintenanceIconState = (value: boolean) => {
    setShowMaintenanceIcon(value);
  };

  return (
    <>
      <ProdPageHeader
        productCardId={productCardId}
        showMaintenanceIcon={showMaintenanceIcon}
        incrementProcessStepIndex={incrementProcessStepIndex}
        decrementProcessStepIndex={decrementProcessStepIndex}
        orderStepTrackerResponseItem={orderStepTrackerResponseItem}
        closeProdPageModal={closeProdPageModal}
        isLoadingOrderCodes={isLoadingOrderCodes}
        navigateOrder={navigateOrder}
      />

      {!isLoadingOrderData ? (
        <ProdPageContent
          handleLocked={handleLocked}
          locked={locked}
          handleshowMaintenanceIconState={handleshowMaintenanceIconState}
          orderStepTrackerResponseItem={orderStepTrackerResponseItem}
          orderProcessStepInfoBox={orderProcessStepInfoBox}
          processStepId={processStepId}
          orderId={orderId}
          openProducedItemInOrderModal={openProducedItemInOrderModal}
          refetchOrderProcessStepTracker={refetchOrderProcessStepTracker}
        />
      ) : (
        <Loader />
      )}

      {producedItemInOrderModal && (
        <ProducedItemsInOrdersModal
          isOpenModal={producedItemInOrderModal}
          hideProducedItemInOrderModal={hideProducedItemInOrderModal}
          processStepId={processStepId}
        />
      )}
    </>
  );
};

export { DynamicProcessStepsForTheOrder };
